import React, { useState } from 'react';
import { HiOutlineCheckBadge, HiOutlineTrash, HiOutlineUserGroup, HiOutlinePencilSquare, HiOutlineUserPlus, HiOutlineCheckCircle, HiOutlinePauseCircle, HiOutlineEnvelopeOpen } from 'react-icons/hi2';
import { useStaffUsers } from '../../../../hooks';
import { DAV_APIS } from '../../../../adapters';
import { StaffUserProps, STAFF_ROLE_OPTIONS } from '../../../../constants';
import { Alert, Modal } from '../../../commons';
import { Card } from '../../../ui/Card';
import StaffEditModal from './StaffEditModal';
import InviteStaffModal from './InviteStaffModal';

interface ActionButtonsProps {
  user: StaffUserProps;
  isPending: boolean;
  updatingId: string | null;
  onToggleActive: (user: StaffUserProps) => void;
  onResendInvite: (user: StaffUserProps) => void;
  onEdit: (user: StaffUserProps) => void;
  onDelete: (user: StaffUserProps) => void;
}

const IconBtn = ({
  icon,
  label,
  onClick,
  disabled,
  colorClass,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  colorClass: string;
}) => (
  <div className='relative group/tip'>
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-lg transition-all disabled:opacity-40 ${colorClass}`}
    >
      {icon}
    </button>
    <span className='pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[11px] font-bold text-white bg-slate-800 rounded-md whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg'>
      {label}
    </span>
  </div>
);

const ActionButtons = ({ user, isPending, updatingId, onToggleActive, onResendInvite, onEdit, onDelete }: ActionButtonsProps) => (
  <div className='flex items-center gap-1'>
    <IconBtn
      icon={<HiOutlinePencilSquare className='w-4 h-4' />}
      label='Edit'
      onClick={() => onEdit(user)}
      colorClass='text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20'
    />
    <IconBtn
      icon={<HiOutlineEnvelopeOpen className='w-4 h-4' />}
      label='Resend Invite'
      onClick={() => onResendInvite(user)}
      disabled={updatingId === user.id}
      colorClass='text-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
    />
    {isPending ? (
      <IconBtn
        icon={<HiOutlineCheckCircle className='w-4 h-4' />}
        label='Approve'
        onClick={() => onToggleActive(user)}
        disabled={updatingId === user.id}
        colorClass='text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
      />
    ) : (
      <IconBtn
        icon={<HiOutlinePauseCircle className='w-4 h-4' />}
        label='Deactivate'
        onClick={() => onToggleActive(user)}
        disabled={updatingId === user.id}
        colorClass='text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
      />
    )}
    <IconBtn
      icon={<HiOutlineTrash className='w-4 h-4' />}
      label='Delete'
      onClick={() => onDelete(user)}
      colorClass='text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
    />
  </div>
);

interface UserRowProps extends ActionButtonsProps {
  onRoleChange: (user: StaffUserProps, role: string) => void;
}

const UserRow = ({ user, isPending, updatingId, onToggleActive, onResendInvite, onEdit, onDelete, onRoleChange }: UserRowProps) => (
  <tr className='border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/20'>
    <td className='px-6 py-4 font-medium text-gray-900 dark:text-white'>{user.username}</td>
    <td className='px-6 py-4 text-gray-600 dark:text-gray-400'>{user.email}</td>
    <td className='px-6 py-4'>
      <select
        value={user.role}
        disabled={updatingId === user.id}
        onChange={e => onRoleChange(user, e.target.value)}
        className='text-xs bg-gray-100 dark:bg-gray-800 border-none rounded-lg px-2 py-1 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500'
      >
        {STAFF_ROLE_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </td>
    {isPending && (
      <td className='px-6 py-4'>
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.isEmailVerified ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
          {user.isEmailVerified ? 'Verified' : 'Unverified'}
        </span>
      </td>
    )}
    <td className='px-6 py-4'>
      <ActionButtons
        user={user}
        isPending={isPending}
        updatingId={updatingId}
        onToggleActive={onToggleActive}
        onResendInvite={onResendInvite}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </td>
  </tr>
);

const StaffPage = () => {
  const [refresh, setRefresh] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<StaffUserProps | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const { staffUsers, loading } = useStaffUsers(refresh);

  const handleRefresh = () => setRefresh(Math.random());

  const handleToggleActive = async (user: StaffUserProps) => {
    setUpdatingId(user.id);
    try {
      await DAV_APIS.agents.activateDeactivateStaffUser(user.id, user.isActive ? 'deactivate' : 'activate');
      Alert({ message: `User ${user.isActive ? 'deactivated' : 'approved'} successfully`, variant: 'success' });
      handleRefresh();
    } catch {
      Alert({ message: 'Failed to update user status', variant: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRoleChange = async (user: StaffUserProps, newRole: string) => {
    setUpdatingId(user.id);
    try {
      await DAV_APIS.agents.updateStaffRole(user.id, newRole);
      Alert({ message: 'Role updated successfully', variant: 'success' });
      handleRefresh();
    } catch {
      Alert({ message: 'Failed to update role', variant: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsLoading(true);
    try {
      await DAV_APIS.agents.deleteStaffUser(selectedUser.id);
      Alert({ message: 'User deleted successfully', variant: 'success' });
      handleRefresh();
      setShowDeleteModal(false);
    } catch {
      Alert({ message: 'Failed to delete user', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvite = async (user: StaffUserProps) => {
    setUpdatingId(user.id);
    try {
      await DAV_APIS.agents.resendStaffInvitation(user.id);
      Alert({ message: `Invitation resent to ${user.email}`, variant: 'success' });
    } catch {
      Alert({ message: 'Failed to resend invitation', variant: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const openEdit = (user: StaffUserProps) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const openDelete = (user: StaffUserProps) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const pending = staffUsers.filter(u => !u.isActive);
  const active = staffUsers.filter(u => u.isActive);

  const sharedRowProps = {
    updatingId,
    onToggleActive: handleToggleActive,
    onResendInvite: handleResendInvite,
    onEdit: openEdit,
    onDelete: openDelete,
    onRoleChange: handleRoleChange,
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Staff Management
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Invite, approve and manage admin staff accounts.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/10 text-yellow-600 dark:text-yellow-400 rounded-lg text-sm font-medium'>
            <HiOutlineUserGroup className='w-4 h-4' />
            {pending.length} Pending
          </div>
          <div className='flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400 rounded-lg text-sm font-medium'>
            <HiOutlineCheckBadge className='w-4 h-4' />
            {active.length} Active
          </div>
          <button
            type='button'
            onClick={() => setShowInviteModal(true)}
            className='flex items-center gap-2 px-4 py-2 bg-[#10b981] text-white rounded-xl text-sm font-bold shadow hover:opacity-90 transition-all'
          >
            <HiOutlineUserPlus className='w-4 h-4' />
            Invite Staff
          </button>
        </div>
      </div>

      {pending.length > 0 && (
        <Card variant='glass' className='overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
          <div className='px-6 pt-5 pb-3'>
            <h2 className='text-base font-bold text-yellow-600 dark:text-yellow-400'>Pending Approval</h2>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-100 dark:border-gray-800'>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Name</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Email</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Role</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Email Verified</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(user => <UserRow key={user.id} user={user} isPending {...sharedRowProps} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card variant='glass' className='overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
        <div className='px-6 pt-5 pb-3'>
          <h2 className='text-base font-bold text-gray-700 dark:text-gray-300'>Active Staff</h2>
        </div>
        {loading && (
          <div className='px-6 py-8 text-center text-gray-400 text-sm'>Loading...</div>
        )}
        {!loading && active.length === 0 && (
          <div className='px-6 py-8 text-center text-gray-400 text-sm'>No active staff users.</div>
        )}
        {!loading && active.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-100 dark:border-gray-800'>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Name</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Email</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Role</th>
                  <th className='text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {active.map(user => <UserRow key={user.id} user={user} isPending={false} {...sharedRowProps} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selectedUser && showEditModal && (
        <StaffEditModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          user={selectedUser}
          onSuccess={handleRefresh}
        />
      )}

      <InviteStaffModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleRefresh}
      />

      <Modal
        size='sm'
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSuccess={handleDelete}
        loading={isLoading}
        successText='Delete User'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <div className='w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-600 mb-4'>
            <HiOutlineTrash className='w-6 h-6' />
          </div>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Remove Staff User</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed text-sm'>
            Are you sure you want to remove{' '}
            <span className='font-bold text-gray-900 dark:text-white'>{selectedUser?.username}</span>? This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default StaffPage;
